import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Import estático de propósito (não DynamicIcon): sem internet não dá pra baixar ícone sob demanda
import { CloseCircle, TickCircle, Warning2, WifiSquare } from 'iconsax-react';
import { useConnectionStatus } from '@/Hooks/useConnectionStatus';
import { NETWORK_ISSUES } from '@/Constants/networkErrorCodes';

const RESTORED_VISIBLE_MS = 3000;

const VARIANTS = {
  offline: {
    Icon: WifiSquare,
    className: 'bg-red-600 text-white',
    textKey: 'connectionBanner.offline',
    defaultText: 'Você está sem internet. Algumas informações podem não carregar.',
    code: NETWORK_ISSUES.OFFLINE.code
  },
  unstable: {
    Icon: Warning2,
    className: 'bg-amber-400 text-gray-900',
    textKey: 'connectionBanner.unstable',
    defaultText: 'Sua conexão está instável. O carregamento pode demorar.',
    dismissible: true
  },
  restored: {
    Icon: TickCircle,
    className: 'bg-green-600 text-white',
    textKey: 'connectionBanner.restored',
    defaultText: 'Conexão restabelecida.'
  }
};

/**
 * Aviso fixo no topo quando o aparelho está sem internet ou com conexão
 * instável, e um "conexão restabelecida" rápido quando volta ao normal.
 */
const ConnectionBanner = () => {
  const { t } = useTranslation();
  const status = useConnectionStatus();
  const [showRestored, setShowRestored] = useState(false);
  const [dismissedStatus, setDismissedStatus] = useState(null);
  const previousStatus = useRef(status);

  useEffect(() => {
    const wasDegraded = previousStatus.current !== 'online';
    previousStatus.current = status;
    // Nova mudança de estado reabre um aviso que o usuário tinha fechado
    setDismissedStatus(null);

    if (status !== 'online' || !wasDegraded) {
      setShowRestored(false);
      return undefined;
    }

    setShowRestored(true);
    const timer = setTimeout(() => setShowRestored(false), RESTORED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const variantKey = status === 'online' ? (showRestored ? 'restored' : null) : status;
  if (!variantKey || dismissedStatus === variantKey) return null;

  const { Icon, className, textKey, defaultText, code, dismissible } = VARIANTS[variantKey];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`connection-banner fixed inset-x-0 top-0 z-[10000] flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium shadow-md ${className}`}
      style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
    >
      <Icon size={18} color="currentColor" variant="Bold" aria-hidden="true" className="shrink-0" />
      <span>
        {t(textKey, { defaultValue: defaultText })}
        {code && <span className="ml-1 opacity-80">({code})</span>}
      </span>
      {dismissible && (
        <button
          type="button"
          onClick={() => setDismissedStatus(variantKey)}
          className="ml-1 shrink-0 p-1"
          aria-label={t('connectionBanner.dismiss', { defaultValue: 'Fechar aviso' })}
        >
          <CloseCircle size={18} color="currentColor" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default ConnectionBanner;
